import { Convert, weightUnits } from "../components/Unit";

export const calculateLFI = (ci, normalizedMass) => {
  const nm = parseFloat(normalizedMass) || 0;

  if (nm <= 0) {
    console.warn("Invalid normalized mass:", nm);
    return 0;
  }
  const im_mass_virgin_material = calculate_im_mass_virgin_material(normalizedMass, ci.im_virgin_content);

  const eol_recycling_waste = calculate_eol_recycling_waste(normalizedMass, ci.eol_recycling_efficiency, ci.om_recycling);
  const eol_feedstock_waste = calculate_eol_feedstock_waste(normalizedMass, ci.eol_feedstock_efficiency, ci.im_recycled_content);
  const eol_unrecoverable_waste = calculate_eol_unrecoverable_waste(ci.om_landfill_waste, eol_recycling_waste, eol_feedstock_waste);

  const denominator = 2 * nm + (0.5 * (eol_feedstock_waste - eol_recycling_waste)) / 100;

  if (denominator <= 0) {
    console.warn("Invalid denominator:", denominator);
    return 0;
  }
  const LFI = (im_mass_virgin_material + eol_unrecoverable_waste) / denominator / 100;
  return LFI;
};
export const calculateMCI = (LFI, ci) => {
  const lfiValue = parseFloat(LFI) || 0;
  // const ufValue = parseFloat(uf_calculations) || 0;
  const uf_calculations = calculate_uf_calculations(ci);

  if (uf_calculations <= 0) return 0;

  const MCI = 1 - (lfiValue * 0.9) / uf_calculations;
  return Math.max(0, Math.min(1, MCI));
};
export const calculateMassMCIFactor = (quantity, mci) => {
  const q = parseFloat(quantity) || 0;
  const m = parseFloat(mci) || 0;
  return q * m;
};
export const calculate_mass_MCI_factor = (ci) => {
  const q = parseFloat(ci.quantity) || 0;
  const m = parseFloat(ci.MCI) || 0;
  return q * m;
};
export const calculatePercentageMCI = (mci) => {
  const m = parseFloat(mci) || 0;
  const ret = Math.max(0, (m - 0.1) / 0.9);
  return ret;
};
export const calculateProductMCI = (rows) => {
  const mass_sum = sumMass(rows);
  if (mass_sum === 0) return 0;
  const MCI =
    rows.reduce((sum, row) => {
      const mass = row.mass !== undefined && row.mass !== null ? (parseFloat(row.mass) || 0) : calculateMass(row.quantity, row.unit, row.density, row.factor);
      const normalizedMass = calculateNormalizedMass(mass, mass_sum);
      const lfi = calculateLFI(row, normalizedMass);
      const mci = calculateMCI(lfi, row);
      return sum + mass * mci;
    }, 0) / mass_sum;
  return MCI;
};
export const calculatePercentageProductMCI = (rows) => {
  const mass_sum = sumMass(rows);
  if (mass_sum === 0) return 0;
  const MCI =
    rows.reduce((sum, row) => {
      const mass = row.mass !== undefined && row.mass !== null ? (parseFloat(row.mass) || 0) : calculateMass(row.quantity, row.unit, row.density, row.factor);
      const normalizedMass = calculateNormalizedMass(mass, mass_sum);
      const lfi = calculateLFI(row, normalizedMass);
      const mci = calculateMCI(lfi, row);
      const percentage_mci = calculatePercentageMCI(mci);
      return sum + mass * percentage_mci;
    }, 0) / mass_sum;
  return MCI;
};
export const calculateEmboidedCarbonPerCost = (gwp, cost) => {
  const g = parseFloat(gwp) || 0;
  const c = parseFloat(cost) || 0;
  return g / c;
};
export const calculateCostTotal = (cost_per_unit, quantity) => {
  const q = parseFloat(quantity) || 0;
  const c = parseFloat(cost_per_unit) || 0;
  return q * c;
};
export const calculateMass = (quantity, unit, density, factor, mass) => {
  if (mass !== undefined && mass !== null) {
    const m = parseFloat(mass);
    if (!isNaN(m)) return m;
  }
  if (quantity && typeof quantity === 'object') {
    const obj = quantity;
    if (obj.mass !== undefined && obj.mass !== null) {
      const m = parseFloat(obj.mass);
      if (!isNaN(m)) return m;
    }
    return calculateMass(obj.quantity, obj.unit, obj.density, obj.factor);
  }
  const q = parseFloat(quantity) || 0;
  const d = parseFloat(density) || 1;
  const f = parseFloat(factor) || 1;
  if (weightUnits.includes(unit)) {
    return Convert(q * f, unit, "kg");
  } else {
    return q * d * f;
  }
};
export const calculate_mass = (ci) => {
  if (ci && ci.mass !== undefined && ci.mass !== null) {
    const m = parseFloat(ci.mass);
    if (!isNaN(m)) return m;
  }
  const q = parseFloat(ci.quantity) || 0;
  const d = parseFloat(ci.density) || 1;
  const f = parseFloat(ci.factor) || 1;
  if (weightUnits.includes(ci.unit)) {
    return Convert(q * f, ci.unit, "kg");
  } else {
    return q * d * f;
  }
};
export const calculateGWP = (quantity, gwp_unit, factor, uncertainty, unit_conversion_factor = 1.0, type = null, user_factor = 1.0) => {
  const q = parseFloat(quantity) || 0;
  const g = parseFloat(gwp_unit) || 0;
  const f = parseFloat(factor) || 1;
  let u = parseFloat(uncertainty);
  if (isNaN(u) || u === null || u === undefined) {
    u = type === "Transport" ? 1.03 : 1.0;
  } else if (u > 0 && u < 1) {
    u = 1.0 + u;
  } else if (u <= 0) {
    u = type === "Transport" ? 1.03 : 1.0;
  }
  const ucf = parseFloat(unit_conversion_factor) || 1.0;
  const uf = parseFloat(user_factor) !== undefined && parseFloat(user_factor) !== null ? parseFloat(user_factor) : 1.0;
  return q * g * f * u * ucf * uf;
};
export const calculate_gwp_total = (ci) => {
  const q = parseFloat(ci.quantity) || 0;
  const g = parseFloat(ci.gwp_unit) || 0;
  const f = parseFloat(ci.factor) || 1;
  let u = parseFloat(ci.uncertainty);
  if (isNaN(u) || u === null || u === undefined) {
    u = ci.type === "Transport" ? 1.03 : 1.0;
  } else if (u > 0 && u < 1) {
    u = 1.0 + u;
  } else if (u <= 0) {
    u = ci.type === "Transport" ? 1.03 : 1.0;
  }
  const ucf = parseFloat(ci.unit_conversion_factor) || 1.0;
  const uf = parseFloat(ci.user_factor) !== undefined && parseFloat(ci.user_factor) !== null ? parseFloat(ci.user_factor) : 1.0;
  return q * g * f * u * ucf * uf;
};
export const calculateNormalizedMass = (mass, totalMass) => {
  const m = parseFloat(mass) || 0;
  const t = parseFloat(totalMass) || 0;
  if (t === 0) return 0;
  return m / t;
};
export const calculate_normalized_mass = (ci, mass_sum) => {
  const m = parseFloat(ci.mass) || 0;
  const s = parseFloat(mass_sum) || 0;
  if (s === 0) return 0;
  return m / s;
};
export const calculateNormalizedGWP = (gwp, totalGWP) => {
  const g = parseFloat(gwp) || 0;
  const t = parseFloat(totalGWP) || 0;
  if (t === 0) return 0;
  return g / t;
};
export const calculate_normalized_gwp = (ci, gwp_total_sum) => {
  const g = parseFloat(ci.gwp_total) || 0;
  const t = parseFloat(gwp_total_sum) || 0;
  if (t === 0) return 0;
  return g / t;
};
export const calculate_uf_calculations = (ci) => {
  const pl = parseFloat(ci.uf_product_life) || 0;
  const ial = parseFloat(ci.uf_industry_avg_life) || 1; // Default to 1 to avoid div by zero
  const fu = parseFloat(ci.uf_functional_units) || 0;
  const iau = parseFloat(ci.uf_industry_avg_units) || 1; // Default to 1 to avoid div by zero

  // If result is NaN/Infinity, return default value of 1
  const result = (pl / ial) * (fu / iau);
  const finalResult = !isFinite(result) || isNaN(result) || result <= 0 ? 1 : result;

  return finalResult;
};

export const calculate_im_mass_virgin_material = (ci) => {
  const nm = parseFloat(ci.normalized_mass) || 0;
  const ivc = parseFloat(ci.im_virgin_content) || 0;
  const ret = nm * ivc;
  if (ret < 0) {
    return 0;
  }
  return ret;
};
export const calculate_im_virgin_content = (ci) => {
  const irc = parseFloat(ci.im_recycled_content) || 0;
  const iru = parseFloat(ci.im_reuse) || 0;
  const irm = parseFloat(ci.im_remanufactured) || 0;
  const ibm = parseFloat(ci.im_bio_materials) || 0;
  const ret = 100 - (irc + iru + irm + ibm);
  if (ret < 0) {
    return 0;
  }
  return ret;
};
export const calculate_om_waste_content = (ci) => {
  const or = parseFloat(ci.om_recycling) || 0;
  const ou = parseFloat(ci.om_reuse) || 0;
  const obc = parseFloat(ci.om_bio_composted) || 0;
  const obe = parseFloat(ci.om_bio_energy_recovery) || 0;
  const ret = 100 - (or + ou + obc + obe);
  if (ret < 0) {
    return 0;
  }
  return ret;
};
export const calculate_om_landfill_waste = (ci) => {
  const nm = parseFloat(ci.normalized_mass) || 0;
  const owc = parseFloat(ci.om_waste_content) || 0;
  const ret = nm * owc;
  if (ret < 0) {
    return 0;
  }
  return ret;
};
export const calculate_eol_recycling_waste = (ci) => {
  const m = parseFloat(ci.normalized_mass) || 0;
  const eRE = (parseFloat(ci.eol_recycling_efficiency) || 0) / 100;
  const oR = (parseFloat(ci.om_recycling) || 0) / 100;
  const ret = m * ((1 - eRE) * oR);
  return ret * 100;
};
export const calculate_eol_feedstock_waste = (ci) => {
  const m = parseFloat(ci.normalized_mass) || 0;
  let eFE = (parseFloat(ci.eol_feedstock_efficiency) || 0) / 100;
  
  // Safety: If efficiency is 0 or tiny, treat as 100% to avoid infinite waste
  if (eFE <= 0.01) eFE = 1.0; 
  
  const irc = (parseFloat(ci.im_recycled_content) || 0) / 100;
  const ret = (m * ((1 - eFE) * irc)) / eFE;
  return ret * 100;
};

export const calculate_eol_unrecoverable_waste = (ci) => {
  const olw = (parseFloat(ci.om_landfill_waste) || 0) / 100;
  const erw = (parseFloat(ci.eol_recycling_waste) || 0) / 100;
  const efw = (parseFloat(ci.eol_feedstock_waste) || 0) / 100;
  const ret = olw + 0.5 * (erw + efw);
  return ret * 100;
};
export const calculate_lfi = (ci) => {
  const nm = parseFloat(ci.normalized_mass) || 0;

  if (nm <= 0) {
    console.warn("Invalid normalized mass:", nm);
    return 0;
  }
  const im_mass_virgin_material = ci.im_mass_virgin_material;

  const eol_recycling_waste = ci.eol_recycling_waste;
  const eol_feedstock_waste = ci.eol_feedstock_waste;
  const eol_unrecoverable_waste = ci.eol_unrecoverable_waste;
  const denominator = 2 * nm + (0.5 * (eol_feedstock_waste - eol_recycling_waste)) / 100;

  if (denominator <= 0) {
    console.warn("Invalid denominator:", denominator);
    return 0;
  }
  const LFI = (im_mass_virgin_material + eol_unrecoverable_waste) / denominator / 100;
  return LFI;
};
export const calculate_mci = (ci) => {
  const lfi = ci.LFI;
  const uf_calculations = ci.uf_calculations;

  // Safety check: if uf_calculations is invalid, return 0
  if (!isFinite(uf_calculations) || isNaN(uf_calculations) || uf_calculations <= 0) {
    console.warn(`Invalid uf_calculations for ${ci.description}:`, uf_calculations);
    return 0;
  }

  const MCI = 1 - (lfi * 0.9) / uf_calculations;
  return Math.max(0, Math.min(1, MCI));
};
export const calculate_product_mci = (rows) => {
  const MCI = rows.reduce((sum, row) => {
    const normalized_mass = parseFloat(row.normalized_mass) || 0;
    const mci = parseFloat(row.MCI) || 0;
    return sum + normalized_mass * mci;
  }, 0);
  return MCI;
};
export const calculate_percentage_product_mci = (rows) => {
  const MCI = rows.reduce((sum, row) => {
    const normalized_mass = parseFloat(row.normalized_mass) || 0;
    const percentage_mci = parseFloat(row.percentage_mci) || 0;
    return sum + normalized_mass * percentage_mci;
  }, 0);
  return MCI;
};
export const calculate_mci_sum = (rows) => {
  return rows.reduce((sum, row) => {
    const MCI = parseFloat(row.MCI) || 0;
    return sum + MCI;
  }, 0);
};
export const calculate_mci_contribution = (ci, mci_sum) => {
  if (mci_sum === 0) return 0;
  const MCI = parseFloat(ci.MCI) || 0;
  return MCI / mci_sum;
};
export const calculate_cost_total = (ci) => {
  const q = parseFloat(ci.quantity) || 0;
  const c = parseFloat(ci.cost_per_unit) || 0;
  return q * c;
};
export const calculate_embodied_carbon_per_cost = (ci) => {
  const g = parseFloat(ci.gwp_unit) || 0;
  const c = parseFloat(ci.cost_per_unit) || 0;
  // Prevent division by zero - return 0 if cost is 0
  if (c === 0) return 0;
  return g / c;
};

export const calculate_gwp_total_sum = (rows) => {
  return rows.reduce((sum, row) => {
    const gwp_total = parseFloat(row.gwp_total) || 0;
    return sum + gwp_total;
  }, 0);
};
export const sumMass = (rows) => {
  return rows.reduce((sum, material) => {
    if (material && material.mass !== undefined && material.mass !== null) {
      const m = parseFloat(material.mass);
      if (!isNaN(m)) return sum + m;
    }
    return sum + calculateMass(material.quantity, material.unit, material.density, material.factor);
  }, 0);
};
export const sumQuantity = (rows) => {
  return rows.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
};
export const sumMassMCIFactor = (rows) => {
  return rows.reduce((sum, item) => sum + (parseFloat(item.mass_MCI_factor) || 0), 0);
};
export const sumGWPTotal = (rows) => {
  return rows.reduce((sum, item) => {
    if (item.gwp_unit) {
      return sum + (calculateGWP(item.quantity, item.gwp_unit, item.factor, item.uncertainty, item.unit_conversion_factor) || 0);
    }
    if (item.gwp_total) {
      return sum + (parseFloat(item.gwp_total) || 0);
    }
    return sum;
  }, 0);
};
export const sumCostTotal = (rows) => {
  return rows.reduce((sum, item) => sum + (parseFloat(item.cost_total) || 0), 0);
};

export const recalculate_row_mci = (ci) => {
  const row = { ...ci };
  row.uf_calculations = calculate_uf_calculations(row);
  row.im_virgin_content = calculate_im_virgin_content(row);
  row.im_mass_virgin_material = calculate_im_mass_virgin_material(row);
  row.om_waste_content = calculate_om_waste_content(row);
  row.om_landfill_waste = calculate_om_landfill_waste(row);
  row.eol_recycling_waste = calculate_eol_recycling_waste(row);
  row.eol_feedstock_waste = calculate_eol_feedstock_waste(row);
  row.eol_unrecoverable_waste = calculate_eol_unrecoverable_waste(row);
  row.LFI = calculate_lfi(row);
  row.MCI = calculate_mci(row);
  row.percentage_mci = calculatePercentageMCI(row.MCI);
  row.mass_MCI_factor = calculate_mass_MCI_factor(row);
  row.cost_total = calculate_cost_total(row);
  row.embodied_carbon_per_cost = calculate_embodied_carbon_per_cost(row);
  return row;
};

export const calculate_combined_data = (t0) => {

  const t1 = t0.map((item) => {
    const ci = { ...item };
    ci.mass = calculate_mass(ci);
    ci.gwp_total = calculate_gwp_total(ci);
    return ci;
  });
  const mass_sum = sumMass(t0);
  const gwp_total_sum = calculate_gwp_total_sum(t0);
  const quantity_sum = sumQuantity(t0);

  const t2 = t1.map((item) => {
    const ci = { ...item };
    ci.normalized_mass = calculate_normalized_mass(ci, mass_sum);
    ci.normalized_gwp = calculate_normalized_gwp(ci, gwp_total_sum);
    ci.uf_calculations = calculate_uf_calculations(ci);
    ci.im_virgin_content = calculate_im_virgin_content(ci);
    ci.im_mass_virgin_material = calculate_im_mass_virgin_material(ci);
    ci.om_waste_content = calculate_om_waste_content(ci);
    ci.om_landfill_waste = calculate_om_landfill_waste(ci);
    ci.eol_recycling_waste = calculate_eol_recycling_waste(ci);
    ci.eol_feedstock_waste = calculate_eol_feedstock_waste(ci);
    ci.eol_unrecoverable_waste = calculate_eol_unrecoverable_waste(ci);
    ci.LFI = calculate_lfi(ci);
    ci.MCI = calculate_mci(ci);
    ci.percentage_mci = calculatePercentageMCI(ci.MCI);
    ci.row_mci = ci.MCI; // Ensure row_mci maps to calculated MCI for dashboard rendering
    ci.mass_MCI_factor = calculate_mass_MCI_factor(ci);
    ci.cost_total = calculate_cost_total(ci);
    ci.embodied_carbon_per_cost = calculate_embodied_carbon_per_cost(ci);
    return ci;
  });
  const mci_sum = calculate_mci_sum(t2);

  const combined_circularities = t2.map((item) => {
    const ci = { ...item };
    ci.mci_contribution = calculate_mci_contribution(ci, mci_sum);
    ci.row_mci = ci.row_mci || ci.MCI; // Double safety mapping for output variables
    return ci;
  });
  const mass_MCI_factor_sum = sumMassMCIFactor(combined_circularities);
  const cost_sum = sumCostTotal(combined_circularities);
  const product_mci = calculate_product_mci(combined_circularities);
  const percentage_product_mci = Math.max(0, calculate_percentage_product_mci(combined_circularities));
  return { mass_sum, gwp_total_sum, cost_sum, quantity_sum, mass_MCI_factor_sum, mci_sum, product_mci, percentage_product_mci, combined_circularities };
};
